import styled from 'styled-components';
import toColorString from 'polished/lib/color/toColorString';
import rem from 'polished/lib/helpers/rem';
import color from '@decisiv/design-tokens/lib/color';
import spacing from '@decisiv/design-tokens/lib/spacing';
import typography from '@decisiv/design-tokens/lib/typography';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Input from '@decisiv/ui-components/lib/components/TextField/Input';

const line = toColorString(color.opacity.charcoal15);

// Mirrors SearchInput: the input and its trigger share the field's box.
export const FieldBody = styled(Flex)`
  width: 100%;
  align-items: center;
  min-width: 0;
`;

// KDS Input is width: 100%; as a flex sibling of the trigger it must shrink.
export const FieldInput = styled(Input)`
  && {
    flex: 1;
    width: auto;
    min-width: 0;
    padding-right: 0;
  }
`;

export const TriggerSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex: none;
  margin: 0 ${rem(spacing.base * 0.25)} ${rem(spacing.base * 0.5)} 0;
`;

export const Panel = styled.div`
  min-width: ${rem(320)};
  max-width: 90vw;
  display: flex;
  flex-direction: column;
  font-family: ${typography.typeface.default};
`;

export const PanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base * 0.75)};
  padding: ${rem(spacing.base)} ${rem(spacing.base * 1.25)};
  border-bottom: 1px solid ${line};
`;

export const SourceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(spacing.base * 0.5)};
`;

export const SourceChip = styled.button`
  font: inherit;
  font-size: ${rem(typography.size.alias.paragraph2)};
  font-weight: ${typography.weight.alias.semibold};
  line-height: 1.6;
  padding: 0 ${rem(spacing.base)};
  border-radius: ${rem(spacing.base * 1.25)};
  border: 1px solid ${toColorString(color.base.quarterMoon)};
  background: transparent;
  color: ${toColorString(color.base.charcoal)};
  cursor: pointer;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &[aria-pressed='true'] {
    border-color: ${toColorString(color.interaction.pacificOcean)};
    background: ${toColorString(color.interaction.pacificOcean15)};
    color: ${toColorString(color.interaction.pacificOcean)};
  }
`;

export const TreeList = styled.div`
  max-height: ${rem(300)};
  overflow: auto;
  padding: ${rem(spacing.base * 0.5)};
`;

export const TreeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base * 0.5)};
  min-height: ${rem(spacing.base * 2.25)};
  border-radius: ${rem(4)};

  &:hover {
    background: ${toColorString(color.base.fullMoon)};
  }

  &[data-selected='true'] {
    background: ${toColorString(color.interaction.pacificOcean15)};
    box-shadow: inset 2px 0 0 ${toColorString(color.interaction.pacificOcean)};
  }

  &[data-selected='true'] button {
    color: ${toColorString(color.interaction.pacificOcean)};
    font-weight: ${typography.weight.alias.semibold};
  }
`;

const iconButton = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(spacing.base * 1.75)};
  height: ${rem(spacing.base * 1.75)};
  flex: none;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  color: ${toColorString(color.base.alaskanHusky)};
`;

export const ExpandButton = styled.button`
  ${iconButton}
`;

export const ExpandSpacer = styled.span`
  display: inline-block;
  width: ${rem(spacing.base * 1.75)};
  flex: none;
`;

export const PathButton = styled.button`
  font: inherit;
  font-family: ${typography.typeface.mono};
  font-size: ${rem(typography.size.alias.paragraph2)};
  color: ${toColorString(color.base.charcoal)};
  background: transparent;
  border: 0;
  padding: 0 ${rem(spacing.base * 0.25)};
  cursor: pointer;
  text-align: left;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: ${toColorString(color.interaction.pacificOcean)};
  }
`;

export const Required = styled.span`
  color: ${toColorString(color.status.danger.medium)};
`;

export const TypeTag = styled.span`
  font-size: ${rem(10)};
  font-weight: ${typography.weight.alias.semibold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${toColorString(color.base.alaskanHusky)};
  background: ${toColorString(color.base.halfMoon)};
  border-radius: ${rem(3)};
  padding: 0 ${rem(spacing.base * 0.4)};
  line-height: 1.6;
  flex: none;
`;

export const Example = styled.span`
  margin-left: auto;
  padding-right: ${rem(spacing.base * 0.5)};
  font-family: ${typography.typeface.mono};
  font-size: ${rem(11)};
  color: ${toColorString(color.base.alaskanHusky)};
  max-width: 40%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Empty = styled.p`
  margin: 0;
  padding: ${rem(spacing.base * 1.5)};
  text-align: center;
  font-size: ${rem(typography.size.alias.paragraph2)};
  color: ${toColorString(color.base.alaskanHusky)};
`;
